import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/AuthContext";
import LangToggle from "@/components/LangToggle";
import { ShieldCheck, Mic, FileDown, Database, ArrowRight, Lock, CheckCircle2 } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1764314484083-cbd0de7e512c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwzfHxwaHlzaW90aGVyYXBpc3QlMjB3aXRoJTIwcGF0aWVudHxlbnwwfHx8fDE3Nzc3MTA1MjZ8MA&ixlib=rb-4.1.0&q=85";
const FEAT = "https://images.unsplash.com/photo-1762625570087-6d98fca29531?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBjbGluaWMlMjBpbnRlcmlvcnxlbnwwfHx8fDE3Nzc3MTA1MjV8MA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  const { t, user } = useAuth();
  const homeTarget = user ? (user.is_admin ? "/admin" : "/dashboard") : "/login";
  const ctaLabel = user ? (user.is_admin ? "Admin-Zentrale" : t.nav.dashboard) : t.nav.login;

  return (
    <div className="min-h-screen bg-bone text-ink relative overflow-x-hidden">
      {/* Header */}
      <header className="glass fixed top-0 inset-x-0 z-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
            <div className="w-8 h-8 rounded-lg bg-pine flex items-center justify-center text-bone font-heading font-semibold">P</div>
            <span className="font-heading text-lg tracking-tight">PhysioSOAP</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm text-ink-muted">
            <a href="#features" className="hover:text-ink transition">{t.nav.features}</a>
            <a href="#privacy" className="hover:text-ink transition">{t.nav.privacy}</a>
          </nav>
          <div className="flex items-center gap-4">
            <LangToggle />
            <Link
              to={homeTarget}
              className="px-4 py-2 rounded-full bg-pine text-bone text-sm font-medium hover:bg-pine-hover transition"
              data-testid="header-login-btn"
            >{ctaLabel}</Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-32 pb-24 md:pt-40 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="lg:col-span-7"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-pine-50 text-pine text-xs font-semibold tracking-[0.18em] uppercase">
              <ShieldCheck className="w-3.5 h-3.5" strokeWidth={2} />
              {t.landing.eyebrow}
            </div>
            <h1 className="mt-6 text-5xl sm:text-6xl lg:text-7xl tracking-tight font-light leading-[1.05]">
              {t.landing.heroTitle}<br />
              <span className="text-pine font-medium">{t.landing.heroTitle2}</span>
            </h1>
            <p className="mt-8 text-lg text-ink-muted max-w-xl leading-relaxed">{t.landing.heroSub}</p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to={homeTarget}
                className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-pine text-bone font-medium hover:bg-pine-hover transition"
                data-testid="hero-cta-btn"
              >
                {t.landing.ctaPrimary}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition" />
              </Link>
              <a
                href="#features"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-hairline text-ink hover:bg-pine-50 transition"
              >{t.landing.ctaSecondary}</a>
            </div>
            <div className="mt-12 flex flex-wrap gap-x-8 gap-y-3 text-sm text-ink-muted">
              <span className="inline-flex items-center gap-2"><Lock className="w-4 h-4 text-pine" />{t.landing.trust1}</span>
              <span className="inline-flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-pine" />{t.landing.trust2}</span>
              <span className="inline-flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-pine" />{t.landing.trust3}</span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-5"
          >
            <div className="relative rounded-3xl overflow-hidden shadow-floating border border-hairline">
              <img src={HERO} alt="Physiotherapist with patient" className="w-full h-[520px] object-cover" />
              <div className="absolute bottom-4 left-4 right-4 glass rounded-2xl p-4">
                <div className="text-xs uppercase tracking-[0.18em] text-pine font-semibold">SOAP · {new Date().toLocaleDateString()}</div>
                <div className="mt-1 text-sm font-medium text-ink">M. Schmidt — Lendenwirbelsäule</div>
                <div className="mt-2 text-xs text-ink-muted">Plan: Mobilisierung L4/L5, 3× pro Woche, Heimprogramm.</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-24 md:py-32 border-t border-hairline">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-pine">{t.nav.features}</div>
            <h2 className="mt-4 text-4xl sm:text-5xl tracking-tight font-light">{t.landing.featuresTitle}</h2>
          </div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-6">
            <FeatCard className="lg:col-span-5" icon={<Mic className="w-5 h-5" />} title={t.landing.f2Title} desc={t.landing.f2Desc} big />
            <FeatCard className="lg:col-span-7" icon={<Database className="w-5 h-5" />} title={t.landing.f1Title} desc={t.landing.f1Desc} image={FEAT} />
            <FeatCard className="lg:col-span-7" icon={<FileDown className="w-5 h-5" />} title={t.landing.f3Title} desc={t.landing.f3Desc} />
            <FeatCard className="lg:col-span-5" icon={<ShieldCheck className="w-5 h-5" />} title={t.landing.f4Title} desc={t.landing.f4Desc} dark />
          </div>
        </div>
      </section>

      {/* Privacy */}
      <section id="privacy" className="py-24 md:py-32 border-t border-hairline bg-pine text-bone relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-bone/70">{t.nav.privacy}</div>
            <h2 className="mt-4 text-4xl sm:text-5xl tracking-tight font-light">{t.landing.privacyTitle}</h2>
            <p className="mt-6 text-lg text-bone/80 max-w-2xl leading-relaxed">{t.landing.privacyDesc}</p>
          </div>
          <div className="lg:col-span-5 grid grid-cols-2 gap-4">
            {["DSGVO", "HIPAA", "EU-Hosting", "AES-256"].map((b) => (
              <div key={b} className="rounded-2xl border border-bone/15 p-5">
                <div className="text-xs uppercase tracking-[0.18em] text-bone/60">Standard</div>
                <div className="mt-2 font-heading text-2xl">{b}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="py-10 border-t border-hairline">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-wrap items-center justify-between gap-4 text-sm text-ink-muted">
          <span>{t.landing.footer}</span>
          <LangToggle />
        </div>
      </footer>
    </div>
  );
}

function FeatCard({ icon, title, desc, image, big, dark, className = "" }) {
  return (
    <div className={`rounded-3xl border border-hairline p-7 transition hover:shadow-soft hover:-translate-y-0.5 ${dark ? "bg-ink text-bone border-ink" : "bg-surface"} ${className}`}>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${dark ? "bg-bone/10 text-bone" : "bg-pine-50 text-pine"}`}>{icon}</div>
      <h3 className={`mt-5 font-heading tracking-tight ${big ? "text-3xl" : "text-2xl"}`}>{title}</h3>
      <p className={`mt-3 leading-relaxed ${dark ? "text-bone/75" : "text-ink-muted"}`}>{desc}</p>
      {image && (
        <div className="mt-6 rounded-2xl overflow-hidden border border-hairline">
          <img src={image} alt="" className="w-full h-48 object-cover" />
        </div>
      )}
    </div>
  );
}
